import { useState, type FormEvent } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { Alert, Avatar, Box, Button, Card, CardContent, IconButton, InputAdornment, TextField, Typography } from '@mui/material';
import HealthAndSafety from '@mui/icons-material/HealthAndSafety';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { brand } from '../config/appConfig';
import { useAuth } from '../context/AuthContext';
import { formatCuil, validarCuil } from '../utils/identity';

export default function Login() {
  const { session, ficha, accesoDenegado, signIn } = useAuth();
  const navigate = useNavigate();
  const [cuil, setCuil] = useState('');
  const [password, setPassword] = useState('');
  const [verClave, setVerClave] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);

  if (session && ficha) return <Navigate to="/" replace />;

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!validarCuil(cuil)) {
      setError('Revisá el CUIL: no es válido.');
      return;
    }
    if (!password) {
      setError('Ingresá tu contraseña.');
      return;
    }
    setEnviando(true);
    const msg = await signIn(cuil, password);
    setEnviando(false);
    if (msg) setError(msg);
    else navigate('/', { replace: true });
  };

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 2 }}>
      <Card sx={{ maxWidth: 420, width: '100%' }}>
        <CardContent sx={{ p: { xs: 3, sm: 4 } }}>
          <Box sx={{ textAlign: 'center', mb: 3 }}>
            <Avatar sx={{ bgcolor: 'primary.main', width: 64, height: 64, mx: 'auto', mb: 2 }}>
              <HealthAndSafety sx={{ fontSize: 36 }} />
            </Avatar>
            {brand.clientName && (
              <Typography variant="h5" fontWeight={700} color="primary">
                {brand.clientName}
              </Typography>
            )}
            <Typography variant="body1" color="text.secondary">
              Portal de afiliados
            </Typography>
          </Box>

          <form onSubmit={handleSubmit} noValidate>
            {accesoDenegado && !error && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                Esta cuenta no tiene acceso al portal de afiliados. Comunicate con el gremio.
              </Alert>
            )}
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}
            <TextField
              label="CUIL"
              value={cuil}
              onChange={(e) => setCuil(formatCuil(e.target.value))}
              fullWidth
              required
              autoComplete="username"
              inputProps={{ inputMode: 'numeric', maxLength: 13 }}
              placeholder="XX-XXXXXXXX-X"
              sx={{ mb: 2 }}
            />
            <TextField
              label="Contraseña"
              type={verClave ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              fullWidth
              required
              autoComplete="current-password"
              sx={{ mb: 3 }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton aria-label={verClave ? 'Ocultar contraseña' : 'Mostrar contraseña'} onClick={() => setVerClave((v) => !v)} edge="end">
                      {verClave ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <Button type="submit" variant="contained" size="large" fullWidth disabled={enviando}>
              {enviando ? 'Ingresando…' : 'Ingresar'}
            </Button>
          </form>

          <Typography variant="body2" color="text.secondary" sx={{ mt: 3, textAlign: 'center' }}>
            Si todavía no tenés cuenta o no podés ingresar, comunicate con el gremio.
          </Typography>
        </CardContent>
      </Card>
    </Box>
  );
}
